/**
 * Public Savings Calculator — top-of-funnel CFO lead magnet
 * ==========================================================
 * Free. No signup. Visitors plug in cloud spend → get a transparent
 * savings estimate with the math shown. Optional email capture for a
 * tailored plan (lands in the CFO lane of Sales Signals).
 */
import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  TrendingUp, ArrowLeft, Sparkles, Mail, CheckCircle2, Loader2,
  Calculator, DollarSign, Users, ServerCog,
} from 'lucide-react';
import { toast } from 'sonner';
import FAQSection from '../components/FAQSection';
import { faqsByPage } from '../data/faqs';
import BrandTitle from '../components/BrandTitle';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DRIVERS = [
  { key: 'rightsizing',         label: 'Right-size idle / oversized compute',     hint: 'EC2 / RDS instances bigger than they need to be.' },
  { key: 'savings_plans_or_ri', label: 'Use Savings Plans or Reserved Instances', hint: 'Commitment discounts on covered usage.' },
  { key: 'anomaly_catch',       label: 'Catch a runaway job before invoice day', hint: 'Real-time anomaly alerts.' },
  { key: 'customer_pl',         label: 'Spot money-losing customers',             hint: 'Per-customer P&L; pricing power.' },
  { key: 'pr_cost',             label: 'Cost guard at PR time',                   hint: 'Block big infra commits before merge.' },
];

const fmtMoney = (n) => {
  if (n === null || n === undefined || Number.isNaN(n)) return '$0';
  const a = Math.abs(n);
  if (a >= 1_000_000) return `$${(a / 1_000_000).toFixed(1)}M`;
  if (a >= 1_000) return `$${(a / 1_000).toFixed(0)}K`;
  return `$${a.toFixed(0)}`;
};

const SavingsCalculator = () => {
  const [spend, setSpend] = useState(50000);
  const [accounts, setAccounts] = useState(3);
  const [customers, setCustomers] = useState(0);
  const [drivers, setDrivers] = useState({
    rightsizing: true, savings_plans_or_ri: true, anomaly_catch: false,
    customer_pl: false, pr_cost: false,
  });
  const [hasPricing, setHasPricing] = useState(false);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const [emailOpen, setEmailOpen] = useState(false);
  const [emailDone, setEmailDone] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [eEmail, setEEmail] = useState('');
  const [eName, setEName] = useState('');
  const [eCompany, setECompany] = useState('');
  const [eRole, setERole] = useState('');

  const compute = useMemo(
    () => async () => {
      if (!spend || spend <= 0) return;
      setLoading(true);
      try {
        const { data } = await axios.post(`${API_URL}/savings-calculator/estimate`, {
          monthly_cloud_spend_usd: Number(spend),
          num_cloud_accounts: Number(accounts) || 1,
          num_customers: Number(customers) || 0,
          has_pricing_per_customer: hasPricing,
          drivers: Object.entries(drivers).filter(([, v]) => v).map(([k]) => k),
        });
        setResult(data);
      } catch (e) {
        toast.error("Couldn't compute estimate", { description: e?.response?.data?.detail || e?.message });
      } finally { setLoading(false); }
    },
    [spend, accounts, customers, hasPricing, drivers],
  );

  useEffect(() => {
    const t = setTimeout(() => { compute(); }, 300);
    return () => clearTimeout(t);
  }, [compute]);

  const submitEmail = async (e) => {
    e?.preventDefault?.();
    if (!eEmail.trim()) {
      toast.error('A work email helps us reach you');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/savings-calculator/email-plan`, {
        email: eEmail.trim(),
        name: eName.trim() || undefined,
        company: eCompany.trim() || undefined,
        role: eRole.trim() || undefined,
        monthly_cloud_spend_usd: Number(spend),
      });
      setEmailDone(true);
      toast.success('Welcome — plan request received', { description: "We'll be in touch within one business day." });
    } catch (err) {
      toast.error("Couldn't save your request just now", {
        description: err?.response?.data?.detail || err?.message,
      });
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-white text-slate-900" data-testid="savings-calculator-page">
      {/* Top nav */}
      <nav className="fixed top-0 w-full z-50 bg-slate-900 text-slate-100 backdrop-blur-xl border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-3">
              <img src="/icons/cartie-emblem.png" alt="CARTIEAI" className="w-10 h-10 object-contain" />
              <span className="text-xl font-bold">
                <span className="text-white">CARTIE<span className="text-emerald-400">AI</span></span><sup className="ml-0.5 text-[0.55em] font-bold text-emerald-300 align-super tracking-normal">™</sup>
              </span>
            </Link>
            <Link to="/">
              <Button variant="outline" className="border-slate-700 text-slate-400 hover:bg-slate-800">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Home
              </Button>
            </Link>
          </div>
        </div>
      </nav>

      <main className="pt-32 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <Badge className="mb-3 bg-emerald-500/10 text-emerald-300 border-emerald-500/30 tracking-wider">
              <Calculator className="w-3 h-3 mr-1" /> FREE TOOL · NO SIGN-UP
            </Badge>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-slate-900 mb-3"><BrandTitle>How much could you save on cloud?</BrandTitle></h1>
            <p className="text-slate-600 max-w-2xl text-base sm:text-lg leading-relaxed">
              A transparent, conservative estimate based on the latest <span className="text-slate-900 font-medium">FinOps Foundation State of FinOps</span> report and our own design-partner data. No fluff — we show the math, including the parts where the answer is "it depends".
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-6">
            {/* Input panel */}
            <Card className="bg-slate-900 text-slate-100 border-white/10" data-testid="input-card">
              <CardHeader>
                <CardTitle className="text-white text-lg flex items-center gap-2">
                  <ServerCog className="h-5 w-5 text-emerald-400" />Tell us about your setup
                </CardTitle>
                <CardDescription className="text-slate-400">Three numbers and a few checkboxes — we don't need anything sensitive.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-5">
                <div>
                  <label className="text-xs font-medium uppercase tracking-wider text-slate-400 mb-1 block">
                    Monthly cloud spend (USD)
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-2.5 text-slate-400">$</span>
                    <Input
                      type="number"
                      min={1}
                      value={spend}
                      onChange={(e) => setSpend(Number(e.target.value))}
                      data-testid="input-spend"
                      className="pl-7 bg-slate-950 border-slate-700 text-white text-xl font-semibold"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-medium uppercase tracking-wider text-slate-400 mb-1 block">Cloud accounts</label>
                    <Input
                      type="number"
                      min={1}
                      value={accounts}
                      onChange={(e) => setAccounts(Number(e.target.value))}
                      data-testid="input-accounts"
                      className="bg-slate-950 border-slate-700 text-white"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium uppercase tracking-wider text-slate-400 mb-1 block">Active customers (optional)</label>
                    <Input
                      type="number"
                      min={0}
                      value={customers}
                      onChange={(e) => setCustomers(Number(e.target.value))}
                      data-testid="input-customers"
                      className="bg-slate-950 border-slate-700 text-white"
                    />
                  </div>
                </div>
                {customers > 0 && (
                  <label className="flex items-center gap-2 text-sm text-slate-400 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={hasPricing}
                      onChange={(e) => setHasPricing(e.target.checked)}
                      className="accent-emerald-500"
                      data-testid="input-has-pricing"
                    />
                    We already track cost per customer in some form
                  </label>
                )}

                <div>
                  <p className="text-xs font-medium uppercase tracking-wider text-slate-400 mb-2">Where might you save?</p>
                  <div className="space-y-2">
                    {DRIVERS.map((d) => (
                      <label key={d.key} className="flex items-start gap-2 p-2 rounded-md hover:bg-white/5 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={drivers[d.key]}
                          onChange={(e) => setDrivers({ ...drivers, [d.key]: e.target.checked })}
                          className="mt-1 accent-emerald-500"
                          data-testid={`driver-${d.key}`}
                        />
                        <div>
                          <p className="text-sm text-white">{d.label}</p>
                          <p className="text-xs text-slate-400">{d.hint}</p>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Result panel */}
            <Card className="bg-slate-900 border-emerald-500/30 text-slate-100" data-testid="result-card">
              <CardHeader>
                <CardTitle className="text-white text-lg flex items-center gap-2">
                  <TrendingUp className="h-5 w-5 text-emerald-400" />Your estimate
                </CardTitle>
                <CardDescription className="text-slate-400">Updates as you type · conservative midpoint, capped at 40%.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-5">
                {!result ? (
                  <div className="h-48 flex items-center justify-center text-slate-400 text-sm">
                    {loading ? 'Calculating…' : 'Enter your monthly cloud spend to see results'}
                  </div>
                ) : (
                  <>
                    <div>
                      <p className="text-xs uppercase tracking-wider text-slate-400 mb-1">Annual savings (estimate)</p>
                      <p className="text-5xl font-extrabold text-emerald-400 font-mono leading-none" data-testid="annual-savings">
                        {fmtMoney(result.annual_savings_usd)}
                      </p>
                      <p className="text-sm text-slate-400 mt-2">
                        ≈ <span className="text-white font-semibold font-mono" data-testid="monthly-savings">{fmtMoney(result.monthly_savings_usd)}</span>/month —
                        about <span className="text-white font-semibold">{(result.savings_pct * 100).toFixed(0)}%</span> of your bill.
                      </p>
                    </div>

                    <div className="rounded-lg bg-slate-950 text-slate-100 border border-white/5 p-4 space-y-2 text-sm">
                      <p className="text-xs uppercase tracking-wider text-slate-400 font-semibold">Where the % comes from</p>
                      <div className="flex justify-between text-slate-400">
                        <span>FinOps baseline</span>
                        <span className="font-mono">{(result.baseline_pct * 100).toFixed(0)}%</span>
                      </div>
                      {result.drivers_used.map((d) => (
                        <div key={d.driver} className="flex justify-between text-slate-400">
                          <span className="text-emerald-400">+ {DRIVERS.find((x) => x.key === d.driver)?.label || d.driver}</span>
                          <span className="font-mono text-emerald-400">+{d.lift_pct}%</span>
                        </div>
                      ))}
                      <div className="flex justify-between text-slate-400 text-xs italic pt-1 border-t border-white/5">
                        <span>Scale factor (your spend tier)</span>
                        <span className="font-mono">×{result.scale_multiplier}</span>
                      </div>
                    </div>

                    {result.cost_per_customer && (
                      <div className="rounded-lg bg-rose-500/10 border border-rose-500/30 p-4 text-sm" data-testid="cpc-block">
                        <p className="text-xs uppercase tracking-wider text-rose-300 font-semibold mb-1 flex items-center gap-1.5"><Users className="h-3.5 w-3.5" />Customer P&amp;L angle</p>
                        <p className="text-white">
                          Average cost per customer: <span className="font-mono text-rose-300">${result.cost_per_customer.cost_per_customer_usd.toLocaleString()}</span>/mo
                        </p>
                        <p className="text-slate-400 mt-1">
                          Estimated <span className="font-bold text-rose-400">{result.cost_per_customer.estimated_money_losing_customers}</span> customers may be losing you money today.
                        </p>
                        <p className="text-xs text-slate-400 italic mt-2">{result.cost_per_customer.note}</p>
                      </div>
                    )}

                    <div className="flex flex-wrap gap-2 pt-2">
                      <Button
                        onClick={() => { setEmailDone(false); setEmailOpen(true); }}
                        className="bg-emerald-500 hover:bg-emerald-400 text-white"
                        data-testid="email-plan-btn"
                      >
                        <Mail className="h-4 w-4 mr-2" />Email me the detailed plan
                      </Button>
                      <Link to="/customer-pl">
                        <Button variant="outline" className="border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10" data-testid="see-live-demo-btn">
                          <Sparkles className="h-4 w-4 mr-2" />See it live with demo data
                        </Button>
                      </Link>
                    </div>

                    <p className="text-[11px] text-slate-400 italic pt-2 border-t border-white/5">{result.disclaimer}</p>
                  </>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Methodology card */}
          {result && (
            <Card className="mt-6 bg-slate-900 text-slate-100 border-white/10" data-testid="methodology-card">
              <CardHeader>
                <CardTitle className="text-base text-white flex items-center gap-2">
                  <DollarSign className="h-5 w-5 text-emerald-400" />Methodology &amp; honest caveats
                </CardTitle>
              </CardHeader>
              <CardContent className="text-sm text-slate-400 space-y-2">
                <p>{result.methodology}</p>
                <p className="text-xs text-slate-400 italic pt-2 border-t border-white/5">
                  We'd rather be conservative than over-promise. If we win you, we'd love for the actual savings to beat this estimate — and we'll publish the result openly with your permission.
                </p>
              </CardContent>
            </Card>
          )}

          <div className="pt-8">
            <FAQSection
              title="Common questions"
              items={faqsByPage.savings_calc}
              testIdPrefix="savings-faq"
              variant="card"
            />
          </div>
        </div>
      </main>

      {/* Email dialog */}
      <Dialog open={emailOpen} onOpenChange={setEmailOpen}>
        <DialogContent data-testid="email-dialog">
          {emailDone ? (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 text-emerald-400">
                  <CheckCircle2 className="h-5 w-5" />You're on the list
                </DialogTitle>
                <DialogDescription>
                  Our founder will email <strong>{eEmail}</strong> within one business day with a tailored plan based on your numbers — no sales pitch attached, just useful detail.
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button onClick={() => setEmailOpen(false)} data-testid="email-close-btn">Sounds good</Button>
              </DialogFooter>
            </>
          ) : (
            <form onSubmit={submitEmail}>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Mail className="h-5 w-5 text-emerald-500" />Get a tailored savings plan
                </DialogTitle>
                <DialogDescription>
                  Our founder will personally review your inputs and send back a one-page plan within one business day. No marketing list, no auto-sequence.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-3 py-3">
                <div>
                  <label className="text-xs font-medium uppercase tracking-wider text-slate-600">Work email *</label>
                  <Input type="email" required value={eEmail} onChange={(e) => setEEmail(e.target.value)} placeholder="you@yourcompany.com" autoFocus data-testid="email-input" />
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="text-xs font-medium uppercase tracking-wider text-slate-600">Name</label>
                    <Input value={eName} onChange={(e) => setEName(e.target.value)} placeholder="Jane Doe" data-testid="name-input" />
                  </div>
                  <div>
                    <label className="text-xs font-medium uppercase tracking-wider text-slate-600">Company</label>
                    <Input value={eCompany} onChange={(e) => setECompany(e.target.value)} placeholder="Acme Corp" data-testid="company-input" />
                  </div>
                  <div>
                    <label className="text-xs font-medium uppercase tracking-wider text-slate-600">Role</label>
                    <Input value={eRole} onChange={(e) => setERole(e.target.value)} placeholder="CFO / Finance" data-testid="role-input" />
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setEmailOpen(false)} data-testid="email-cancel-btn">Maybe later</Button>
                <Button type="submit" disabled={submitting} className="bg-emerald-500 hover:bg-emerald-400 text-white" data-testid="email-submit-btn">
                  {submitting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Sending…</> : <><Mail className="h-4 w-4 mr-2" />Send me the plan</>}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      <footer className="py-6 bg-slate-950 border-t border-white/5 text-center text-xs text-slate-400">
        © 2026 CARTIEAI · <Link to="/privacy-policy" className="hover:text-slate-600">Privacy</Link> · <Link to="/terms" className="hover:text-slate-600">Terms</Link> · <Link to="/faq" className="hover:text-slate-600">FAQ</Link>
      </footer>
    </div>
  );
};

export default SavingsCalculator;
